import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { io, type Socket } from 'socket.io-client';

const SOCKET_URL = 'https://ws.francestudent.org';
const SOCKET_NAMESPACE = 'vitrine';
const HEARTBEAT_INTERVAL_MS = 15_000;

type PresenceMessage = Array<{
  payload: {
    currentPageTitle: string;
    pathname: string;
    sentAt: string;
    url: string;
  };
}>;

const SocketPresence = () => {
  const location = useLocation();
  const socketRef = useRef<Socket | null>(null);

  const pageContext = useMemo(
    () => ({
      pathname: location.pathname,
      url: `${location.pathname}${location.search}${location.hash}`,
    }),
    [location.hash, location.pathname, location.search]
  );

  useEffect(() => {
    const socket = io(`${SOCKET_URL}/${SOCKET_NAMESPACE}`, {
      transports: ['websocket'],
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;

    if (!socket) {
      return;
    }

    const buildMessage = (): PresenceMessage => [
      {
        payload: {
          currentPageTitle: document.title || 'France Student',
          pathname: pageContext.pathname,
          sentAt: new Date().toISOString(),
          url: pageContext.url,
        },
      },
    ];

    const sendPresence = () => {
      if (!socket.connected) {
        return;
      }

      socket.send(buildMessage());
    };

    const handleConnect = () => {
      sendPresence();
    };

    socket.on('connect', handleConnect);
    sendPresence();

    const intervalId = window.setInterval(sendPresence, HEARTBEAT_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      socket.off('connect', handleConnect);
    };
  }, [pageContext]);

  return null;
};

export default SocketPresence;
